import {
  Component,
  inject,
  OnInit,
  LOCALE_ID,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  signal,
  computed,
} from '@angular/core';
import { displayName, optionalName } from '../../../../validators/name-rules';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';

import {
  UndeliveredReason,
  UpdateReasonPayload,
} from '../../../../pages/admin-dashboard/undelivered-reasons/undelivered-reasons.model';
import { UndeliveredReasonsService } from '../../../../services/undelivered-reasons/undelivered-reasons.service';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { Loading } from '../../../../utils/loading/loading';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { SCOPES } from '../../../../services/scope/scope-constants'; 
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';

@Component({
  selector: 'app-undelivered-reasons-edit-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SuccessfulNotification,
    ConfirmationDialog,
    Error,
    Loading,
    SubmitButton,
    HasScopeDirective,
  ],
  templateUrl: './undelivered-reasons-edit-component.html',
  styleUrl: './undelivered-reasons-edit-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndeliveredReasonsEditComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private undeliveredReasonsService = inject(UndeliveredReasonsService);
  private locale = inject(LOCALE_ID);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  protected readonly SCOPES = SCOPES;

  // Route / Entity State
  reasonId = signal<number | null>(null);
  currentReason = signal<UndeliveredReason | null>(null);

  // Form Controls (Signals)
  reasonAr = signal<string>('');
  reasonEn = signal<string>('');

  // Form Field Interaction State
  reasonArTouched = signal<boolean>(false);
  reasonEnTouched = signal<boolean>(false);

  // Page State
  loading = signal<boolean>(false);
  errorLoadingReason = signal<string | null>(null);
  isNotEditable = signal<boolean>(false);

  // Submit / Modal State
  showConfirmModal = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);

  // Error & Toast State
  apiError = signal<string>('');
  showToast = signal<boolean>(false);
  toastMessage = signal<string>('');

  private navigationReason = this.router.getCurrentNavigation()?.extras?.state?.['reason'] as
    | UndeliveredReason
    | undefined;

  // ================= Computed Field Errors =================
  reasonArErrors = computed(() => {
    const val = this.reasonAr();
    const validArRegex = /^[\u0600-\u06FF\s-']+$/;

    return {
      required: !val || val.trim() === '',
      minlength: val ? val.length < 3 : false,
      maxlength: val ? val.length > 255 : false,
      hasNumbers: /\d/.test(val),
      hasSpacePadding: val.startsWith(' ') || val.endsWith(' '),
      invalidChars: val && val.trim() !== '' ? !validArRegex.test(val) : false,
    };
  });

  reasonEnErrors = computed(() => {
    const val = this.reasonEn();
    const validEnRegex = /^[a-zA-Z\s-]+$/;

    return {
      minlength: val ? val.length < 3 : false,
      maxlength: val ? val.length > 255 : false,
      hasNumbers: /\d/.test(val),
      hasSpacePadding: val.startsWith(' ') || val.endsWith(' '),
      invalidChars: val && val.trim() !== '' ? !validEnRegex.test(val) : false,
    };
  });

  isReasonArInvalid = computed(() => {
    if (!this.reasonArTouched()) return false;
    const errs = this.reasonArErrors();
    return (
      errs.required ||
      errs.minlength ||
      errs.maxlength ||
      errs.hasNumbers ||
      errs.hasSpacePadding ||
      errs.invalidChars
    );
  });

  isReasonEnInvalid = computed(() => {
    if (!this.reasonEnTouched()) return false;
    const errs = this.reasonEnErrors();
    return (
      errs.minlength ||
      errs.maxlength ||
      errs.hasNumbers ||
      errs.hasSpacePadding ||
      errs.invalidChars
    );
  });

  isFormInvalid = computed(() => {
    const arErrs = this.reasonArErrors();
    const enErrs = this.reasonEnErrors();

    const isArValid =
      !arErrs.required &&
      !arErrs.minlength &&
      !arErrs.maxlength &&
      !arErrs.hasNumbers &&
      !arErrs.hasSpacePadding &&
      !arErrs.invalidChars;

    const isEnValid =
      !enErrs.minlength &&
      !enErrs.maxlength &&
      !enErrs.hasNumbers &&
      !enErrs.hasSpacePadding &&
      !enErrs.invalidChars;

    return !(isArValid && isEnValid);
  });

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsedId = Number(idParam);

    if (!idParam || isNaN(parsedId)) {
      this.errorLoadingReason.set(
        this.isArabic() ? 'معرّف السبب غير صالح' : 'Invalid reason id'
      );
      return;
    }

    this.reasonId.set(parsedId);

    if (this.navigationReason && this.navigationReason.id === parsedId) {
      this.applyLoadedReason(this.navigationReason);
    } else {

      this.fetchReasonFromList(parsedId);
    }
  }

  private fetchReasonFromList(id: number): void {
  this.loading.set(true);
  this.errorLoadingReason.set(null);

  this.undeliveredReasonsService
    .getAllReasons({})
    .pipe(
      finalize(() => {
        this.loading.set(false);
        this.cdr.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe({
      next: (res) => {

        const matchedReason = res?.list?.find((item) => item.id === id);

        if (matchedReason) {
          this.applyLoadedReason(matchedReason);
        } else {
          this.errorLoadingReason.set(
            this.isArabic()
              ? 'لم يتم العثور على هذا السبب ضمن قائمة الأسباب.'
              : 'Reason not found in the list.'
          );
        }
      },
      error: (err) => {
        console.error('Error fetching reasons list:', err);
        this.errorLoadingReason.set(
          this.isArabic()
            ? 'تعذر تحميل بيانات السبب. برجاء العودة لصفحة القائمة والمحاولة مرة أخرى.'
            : 'Could not load reason data. Please go back to the list page and try again.'
        );
      },
    });
}

  isArabic(): boolean {
    return typeof this.locale === 'string' && this.locale.startsWith('ar');
  }

  private applyLoadedReason(reason: UndeliveredReason): void {
    this.currentReason.set(reason);

    if (!reason.isEditable) {
      this.isNotEditable.set(true);
      this.errorLoadingReason.set(
        this.isArabic()
          ? 'لا يمكن تعديل سبب عدم التسليم هذا لأنه معرّف من النظام.'
          : 'This undelivered reason cannot be edited because it is system-defined.'
      );
      this.cdr.markForCheck();
      return;
    }

    this.reasonAr.set(reason.titleAr ?? '');
    this.reasonEn.set(reason.titleEn ?? '');
    this.cdr.markForCheck();
  }

  markArTouched(): void {
    this.reasonArTouched.set(true);
  }

  markEnTouched(): void {
    this.reasonEnTouched.set(true);
  }

  // ================= Submit Flow =================
  onSubmit(): void {
    this.reasonArTouched.set(true);
    this.reasonEnTouched.set(true);

    if (this.isFormInvalid()) {
      return;
    }

    this.apiError.set('');
    this.showConfirmModal.set(true);
  }

  closeConfirmModal(): void {
    this.showConfirmModal.set(false);
  }

  confirmSubmit(): void {
    const id = this.reasonId();
    if (id === null) return;

    this.isSubmitting.set(true);
    this.apiError.set('');

    const payload: UpdateReasonPayload = {
      titleEn: optionalName(this.reasonEn()),
      titleAr: this.reasonAr().trim(),
    };

    this.undeliveredReasonsService
      .updateReason(id, payload)
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
          this.showConfirmModal.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          const reasonName = displayName(this.isArabic(), payload.titleAr, payload.titleEn);
          this.toastMessage.set(
            this.isArabic()
              ? `تم تحديث سبب عدم التسليم '${reasonName}' بنجاح`
              : `Undelivered reason '${reasonName}' updated successfully`
          );

          this.showToast.set(true);
          this.cdr.markForCheck();

          setTimeout(() => {
            this.showToast.set(false);
            this.cdr.markForCheck();
            this.navigateBack();
          }, 2000);
        },
        error: (err) => {
          const hasFieldError = !!(err.error?.errors?.titleAr || err.error?.errors?.titleEn);

          if (!hasFieldError) {
            this.apiError.set(
              this.isArabic()
                ? err.error?.messageAr || 'حدث خطأ أثناء تحديث السبب. حاول مرة أخرى.'
                : err.error?.messageEn || 'An error occurred while updating the reason. Please try again.'
            );
          }
          this.cdr.markForCheck();
        },
      });
  }

  onCancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }
}